import { showNotification } from '@mantine/notifications';
import { QueryClient, QueryKey } from '@tanstack/react-query';
import {
  get,
  isEmpty,
  isNull,
  isObject,
  isUndefined,
  set,
  escapeRegExp,
} from 'lodash';
import { ZodError, ZodType } from 'zod';
type AnyZodType = ZodType<any, any, any>;

export const isFalsy = (obj: any, undefinedOnly = true) =>
  undefinedOnly
    ? isUndefined(obj)
    : isUndefined(obj) ||
      isNull(obj) ||
      obj === '' ||
      (isObject(obj) && isEmpty(obj));

export const withoutUndefined = (obj: any, withoutEmpty = false) => {
  if (!obj || Array.isArray(obj) || typeof obj !== 'object') {
    return obj;
  }

  return Object.keys(obj).reduce((cleanedObj, key: string) => {
    if (isFalsy(obj[key], !withoutEmpty)) {
      return cleanedObj;
    } else {
      cleanedObj[key] = obj[key];
      return cleanedObj;
    }
  }, {} as any);
};

// React Query hands one error to every observer of a failed query, and a
// response's body can be read only once.
const bodies = new WeakMap<Response, Promise<string>>();
const bodyOf = (response: Response) => {
  let body = bodies.get(response);
  if (!body) {
    body = response.text().catch(() => '');
    bodies.set(response, body);
  }
  return body;
};

const errorMessageOf = (status: number, body: string) => {
  try {
    const { message, error } = JSON.parse(body);
    if (typeof message === 'string') return message;
    // A 401 with no message is authMiddleware's: there is no session.
    if (status !== 401 && typeof error === 'string') return error;
  } catch {
    // A gateway in front of the API (CloudFront, the load balancer) answers
    // with an HTML page, not the API's JSON.
  }
  if (status === 401) return "You're signed out. Sign in again and retry.";
  return "The server didn't finish this request. Try again in a moment.";
};

export const mutationErrorHandler = (error: any) => {
  const response: Response | undefined = error.response;
  if (response) {
    bodyOf(response).then((body) =>
      showNotification({
        title: `${response.status}: ${response.statusText}`,
        message: errorMessageOf(response.status, body),
        color: 'red',
      })
    );
  } else {
    showNotification({
      title: error.name ?? 'Unknown error',
      message: error.message,
      color: 'red',
    });
  }
};

export const queryResponseValidator =
  <T>(schema: AnyZodType) =>
  (response: any): T => {
    try {
      return schema.parse(response) as T;
    } catch (err: any) {
      if (err instanceof ZodError) {
        // Log every issue before collapsing to one. A schema mismatch is
        // usually deploy skew — this app talks to an API version it was not
        // built against — and the thrown Error keeps only the first issue.
        // Views that render an empty state on error otherwise leave nothing on
        // screen or in the console to diagnose from.
        console.error(
          'Response failed schema validation:',
          err.issues.map((i) => `${i.path.join('.')}: ${i.message}`)
        );
        const message = `${err.issues[0].message}, at path '${err.issues[0].path.join('.')}'`;
        throw new Error(message);
      }
      throw err;
    }
  };

const flattenObject = (obj: Record<string, any>, parentKey = ''): string[] => {
  const keyValuePairs: string[] = [];

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      const value = obj[key];

      if (value === null) {
        keyValuePairs.push(`${encodeURIComponent(fullKey)}=null`);
      } else if (value instanceof Date) {
        keyValuePairs.push(
          `${encodeURIComponent(fullKey)}=${encodeURIComponent(value.toISOString())}`
        );
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        keyValuePairs.push(...flattenObject(value, fullKey));
      } else if (Array.isArray(value)) {
        keyValuePairs.push(...flattenArray(value, fullKey));
      } else if (value !== undefined) {
        keyValuePairs.push(
          `${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`
        );
      }
    }
  }

  return keyValuePairs;
};

const flattenArray = (arr: Array<any>, parentKey: string): string[] => {
  return arr.flatMap((item, index): string[] => {
    if (item === null) {
      return [`${encodeURIComponent(parentKey)}[]=null`];
    } else if (typeof item === 'object' && !Array.isArray(item)) {
      return flattenObject(item, `${parentKey}[${index}]`);
    } else if (item !== undefined) {
      return [
        `${encodeURIComponent(parentKey)}[]=${encodeURIComponent(String(item))}`,
      ];
    }
    return [];
  });
};

export const toQueryStr = (obj: Record<string, any>): string => {
  return flattenObject(obj).join('&');
};

const parseValue = (value: any) => {
  if (value === 'null') {
    return null;
  } else if (value === 'true') {
    return true;
  } else if (value === 'false') {
    return false;
  } else if (!isNaN(Number(value)) && value !== '') {
    return Number(value);
  } else {
    return value;
  }
};

export const fromQueryStr = <T = any>(queryString: string, schema?: any) => {
  const result = queryString.split('&').reduce((obj, pair) => {
    const [key, value] = pair.split('=').map(decodeURIComponent);

    const parsedValue = parseValue(value);

    if (key.endsWith('[]')) {
      const arrayKey = key.slice(0, -2);
      const existingValue = get(obj as any, arrayKey, []);
      set(obj as any, arrayKey, [...existingValue, parsedValue]);
      return obj;
    }

    set(obj as any, key, parsedValue);
    return obj;
  }, {} as T);

  return schema ? queryResponseValidator<T>(schema)(result) : result;
};

export function getLatestQuery(queryClient: QueryClient, key: QueryKey) {
  const queries = queryClient.getQueryCache().findAll(key);

  if (!queries || queries.length === 0) {
    return null;
  }

  const latestQuery = queries.reduce((latest, current) => {
    // @ts-ignore: updatedAt is not a recognized property on QueryObject but exists in the implementation
    return current.state.updatedAt > latest.state.updatedAt ? current : latest;
  }, queries[0]);

  return latestQuery;
}

export const strToBoolean = (value: string | boolean) => {
  return typeof value === 'string' ? value.toLowerCase() === 'true' : value;
};

export const replacePlaceholders = (
  str: string,
  placeholders: Record<string, string>,
  startDelimiter = '[',
  endDelimiter = ']'
): string => {
  const re = new RegExp(
    `${escapeRegExp(startDelimiter)}(${Object.keys(placeholders).join('|')})${escapeRegExp(
      endDelimiter
    )}`,
    'g'
  );

  return str.replace(re, ($0, $1) => placeholders[$1] ?? $0);
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Build Logz.io Discover URL filtered by traceId.
 * When eventDate is provided, the time picker is set to a range around that date (±1 day)
 * so logs for older events are visible. Otherwise uses a default range (start of current month to now).
 */
export const getLogzIoUrl = (
  traceId: string,
  eventDate?: Date | number | string
) => {
  let timeParam: string;
  if (eventDate != null) {
    const t = new Date(eventDate).getTime();
    if (!Number.isNaN(t)) {
      const from = new Date(t - ONE_DAY_MS).toISOString();
      const to = new Date(t + ONE_DAY_MS).toISOString();
      const _g = `(refreshInterval:(display:Off,section:0,value:0),time:(from:'${from}',to:'${to}'))`;
      timeParam = `_g=${encodeURIComponent(_g)}`;
    } else {
      timeParam =
        '_g=(refreshInterval:(display:Off,section:0,value:0),time:(from:now-1M,to:now))';
    }
  } else {
    timeParam =
      '_g=(refreshInterval:(display:Off,section:0,value:0),time:(from:now-1M,to:now))';
  }
  const _a = `(columns:!(message),filters:!(('$state':(store:appState),meta:(alias:!n,disabled:!f,index:'logzioCustomerIndex*',key:traceId,negate:!f,params:(query:'${traceId}'),type:phrase),query:(match_phrase:(traceId:'${traceId}')))),index:'logzioCustomerIndex*',interval:auto,query:(language:lucene,query:''),sort:!(!('@timestamp',desc)))`;
  return `https://app.logz.io/#/dashboard/osd/discover/?_a=${encodeURIComponent(_a)}&${timeParam}`;
};
